import { DataGrid, Spinner, TooltipV2, BadgeV3 } from '@cimplico/components';
import {
    GenericWorksheet,
    ReconciliationWorksheet,
    WorksheetEntryTypeEnum,
    WorksheetEntryV2,
    HandleLinkedResourceAction,
    HandleLinkedResourceType,
} from '../../../types/worksheet';
import { useRef, useState } from 'react';
import AttachmentMenu from './attachments/AttachmentMenu';
import Currency from '../../../components/currency/Currency';
import { NatureEnum } from '../../../types/nature';
import Dropzone from '../../../components/dropzone/Dropzone';
import { useCreateDocument } from '../../../queries/use-create-document';
import { toastNotification } from '../../../components/toast/Toast';
import { useUpdateWorksheetEntries } from '../../../queries/use-update-worksheet-entries';
import queryClient from '../../../query-client';
import { PREFERRED_LOCALE } from '../../../constants';
import { LockClosedIcon } from '@heroicons/react/20/solid';
import { LinkSlashIcon, ForwardIcon } from '@heroicons/react/24/outline';
import { isFulfilled } from '../../../utilities/promise-fulfilled';
import { useHandleLinkedResource } from '../../../queries/use-handle-linked-resource';

type EntryRowProps = {
    worksheet: GenericWorksheet | ReconciliationWorksheet;
    entry: WorksheetEntryV2;
    id: string;
    onAttach: (attachments: string[]) => void;
    disableAttachments: boolean;
    disabled: boolean;
    priorEntry?: boolean;
    preventDrag: boolean;
    showAttachmentWindow: boolean;
    onAttachmentWindowToggle: (open: boolean) => void;
};

const FILE_LIMIT = 30;

function EntryRow({
    worksheet,
    entry,
    id,
    onAttach,
    disableAttachments,
    disabled,
    priorEntry = false,
    preventDrag,
    showAttachmentWindow,
    onAttachmentWindowToggle,
}: EntryRowProps): JSX.Element {
    const ref = useRef<HTMLParagraphElement | null>(null);

    const [uploading, setUploading] = useState(false);
    const [unlinking, setUnlinking] = useState(false);
    const createDocumentQuery = useCreateDocument();
    const updateEntriesQuery = useUpdateWorksheetEntries(worksheet.id);

    const handleLinkedResourceQuery = useHandleLinkedResource(worksheet.id, entry.source?.id);

    /**
     * Uploads files and automatically attaches them to the entry.
     *
     * @param files File[]
     */
    async function handleFileUpload(files: File[]): Promise<void> {
        setUploading(true);
        const uploads = [];
        for (const file of files) {
            const uploadPromise = createDocumentQuery.mutateAsync(
                { file, parentFolderId: worksheet.id },
                {
                    onSuccess: () => {
                        toastNotification('success', 'New file uploaded!');
                    },
                }
            );
            uploads.push(uploadPromise);
        }
        const records = await Promise.allSettled(uploads);

        const resolvedRecords = records.filter(isFulfilled);

        updateEntriesQuery.mutate(
            [{ id: entry.id, fields: { attachments: [...entry.attachments, ...resolvedRecords.map((r) => r.value.id)] } }],
            {
                onSuccess: () => {
                    setUploading(false);
                },
            }
        );

        queryClient.invalidateQueries(['worksheets', worksheet.id]);
    }

    function unlink(entry: WorksheetEntryV2): void {
        if (entry.source?.metadata.fieldId) {
            setUnlinking(true);
            handleLinkedResourceQuery.mutate(
                {
                    action: HandleLinkedResourceAction.DELETE,
                    propertyId: entry.source.metadata.fieldId,
                    resourceType: HandleLinkedResourceType.ENTRY,
                    resourceId: entry.id,
                },
                {
                    onSuccess: () => {
                        toastNotification('success', 'Entry successfully unlinked from the calculation.');
                    },
                    onError: () => {
                        toastNotification('error', 'Could not unlink entry from the calculation.');
                    },
                    onSettled: () => {
                        setUnlinking(false);
                    },
                }
            );
        }
    }

    function isOverflown(element: HTMLParagraphElement | null): boolean {
        if (element === null) {
            return false;
        }

        return element.scrollWidth > element.clientWidth;
    }

    const descriptionOverflowing = isOverflown(ref.current);

    return (
        <Dropzone onDrop={handleFileUpload} fileLimit={FILE_LIMIT} disabled={disabled}>
            <DataGrid.Row id={id} preventDrag={preventDrag}>
                <div className="flex items-center gap-2 relative">
                    {entry.priorEntryId && !priorEntry && (
                        <div className="absolute -left-8">
                            <TooltipV2 message="This entry has been carried forward">
                                <ForwardIcon className="text-indigo-600 h-4 w-4" />
                            </TooltipV2>
                        </div>
                    )}
                    <p className="text-xs text-gray-700">{new Date(entry.date).toLocaleDateString(PREFERRED_LOCALE)}</p>
                </div>
                <p className="text-xs text-gray-700 inline-flex items-center">
                    ENT#{entry.reference.toString().padStart(3, '0')}
                    {entry.source &&
                        (!priorEntry ? (
                            unlinking ? (
                                <div className="mt-2">
                                    <Spinner size="sm" />
                                </div>
                            ) : (
                                <TooltipV2 message="This entry has been created by a calculation. Click to unlink.">
                                    <button onClick={() => unlink(entry)} className="ml-1 inline-flex items-center">
                                        <LinkSlashIcon className="text-indigo-600 h-4 w-4 mt-1" />
                                    </button>
                                </TooltipV2>
                            )
                        ) : (
                            <TooltipV2 message="Cannot carry forward linked entry.">
                                <button disabled={true} className="ml-1 inline-flex items-center">
                                    <LinkSlashIcon className="text-indigo-600 h-4 w-4 mt-1" />
                                </button>
                            </TooltipV2>
                        ))}
                </p>
                <div>
                    <TooltipV2
                        disabled={Boolean(entry.description) && !descriptionOverflowing}
                        message={entry.description as string}
                        position="right"
                    >
                        <p className="text-xs text-gray-700" ref={ref}>
                            {entry.description || '-'}
                        </p>
                    </TooltipV2>
                </div>

                <div className={entry.type === WorksheetEntryTypeEnum.GENERIC_ENTRY ? 'mr-6' : ''}>
                    {uploading ? (
                        <div className="my-1">
                            <Spinner size="sm" />
                        </div>
                    ) : (
                        <AttachmentMenu
                            attachments={entry.attachments}
                            worksheetFiles={worksheet.files}
                            defaultFolderId={worksheet.id}
                            onAttach={onAttach}
                            disabled={disableAttachments}
                            isOpen={showAttachmentWindow}
                            onOpenToggle={onAttachmentWindowToggle}
                        />
                    )}
                </div>

                {entry.type === WorksheetEntryTypeEnum.RECONCILIATION_ENTRY && (
                    <Currency
                        className="text-xs text-gray-700"
                        value={entry.amount}
                        withParentheses={Boolean(entry.nature === NatureEnum.CREDIT)}
                    />
                )}
            </DataGrid.Row>
        </Dropzone>
    );
}

export default EntryRow;
